import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { fetchGroups, fetchPage } from '@/lists/api'
import { CellValue, cardHref } from '@/lists/cellValue'
import type { ListRow } from '@/lists/types'
import { HistoryPanel } from './HistoryPanel'
import { fetchSubtree, moveDepartment, type DepartmentNode } from './hrApi'
import {
  FormModal,
  HrFailure,
  HrSkeleton,
  OutcomeAlerts,
  RefLink,
  Section,
  StatusBadge,
  day,
  failureText,
  nameOf,
  text,
  useEmployeeNames,
  useOutcome,
} from './hrShared'
import { RecordPicker } from './RecordPicker'
import { Facts, Panel, RecordHeader, RecordTabs, StateView, type Fact, type MenuAction } from './record'
import { initials, labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import '@/styles/record.css'

/**
 * Карточка подразделения на дизайн-системе (Department.dc.html): оргструктура **деревом**.
 *
 * Дерево — поддерево этого подразделения, как его отдаёт сервер: узлы в порядке обхода, с
 * уровнем, посчитанным по материализованному пути на сервере. Отступ рисуется по этому
 * уровню (`--lvl`), а не пересчётом пути здесь: вторая реализация разбора разошлась бы
 * молча — на подразделении, чьё имя содержит разделитель. Сворачивание ветки тоже идёт по
 * уровню: скрываются узлы после свёрнутого, пока уровень глубже его.
 *
 * Перемещение узла переносит **поддерево целиком** одной транзакцией на сервере: оставить
 * пути детей устаревшими значит молча сломать область видимости «своё подразделение».
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - «Добавить сотрудника», «Открыть вакансию», «Переименовать», «Сменить руководителя»,
 *   «Расформировать»: REST не выводит ни создания и правки подразделения, ни приёма из
 *   карточки отдела (`POST /departments` в #68 не выводился).
 * - Штат (ставки по штатному расписанию), ФОТ и оклады сотрудников отдела: штатного
 *   расписания в API нет, а оклад живёт в истории назначений, которой нет в списках.
 *   Численность здесь — фактическая, по сотрудникам, видимым вам.
 * - Дерево всей компании: сервер отдаёт поддерево узла, а не корень организации.
 */
export function DepartmentRecordView({ recordId, record, context, form }: RecordViewProps) {
  const { t, user } = useApp()
  const values = record.values
  const [tab, setTab] = useState('structure')
  const [moving, setMoving] = useState(false)
  const mayMove = can(user, 'hr.department', 'update')
  const mayReadVacancies = can(user, 'hr.vacancy', 'read')

  const subtree = useQuery({
    queryKey: ['hr-subtree', recordId],
    queryFn: ({ signal }) => fetchSubtree(recordId, signal),
    retry: false,
  })
  const ids = (subtree.data ?? []).map((node) => node.id)
  const headcount = useQuery({
    queryKey: ['hr-dept-headcount', ids],
    enabled: ids.length > 0,
    retry: false,
    queryFn: ({ signal }) =>
      fetchGroups(
        'hr.employee',
        {
          columns: ['id'],
          filters: [
            { column: 'department_id', operator: 'in', value: ids },
            { column: 'status', operator: 'ne', value: 'terminated' },
          ],
          sort: [],
          groupBy: 'department_id',
        },
        signal,
      ),
  })
  const vacancies = useQuery({
    queryKey: ['hr-dept-vacancies', recordId],
    enabled: mayReadVacancies,
    retry: false,
    queryFn: ({ signal }) =>
      fetchPage(
        'hr.vacancy',
        {
          columns: ['title', 'status', 'opened_on', 'target_close_on', 'recruiter_id'],
          filters: [
            { column: 'department_id', operator: 'eq', value: recordId },
            { column: 'status', operator: 'eq', value: 'open' },
          ],
          sort: [{ column: 'opened_on', descending: false }],
          pageSize: 20,
        },
        null,
        signal,
      ),
  })
  const outcome = useOutcome([
    ['hr-subtree', recordId],
    ['record', 'hr.department', recordId],
    ['list', 'hr.department'],
  ])

  const listTo = listPath('hr.department', '/departments')
  if (subtree.isLoading) return <HrSkeleton label={t('record.hr.dept.loading')} />
  if (!subtree.data) {
    return <HrFailure error={subtree.error} listTo={listTo} onRetry={() => void subtree.refetch()} />
  }
  const nodes = subtree.data
  const counts = new Map<string, number>()
  for (const group of headcount.data?.groups ?? []) {
    if (typeof group.value === 'string') counts.set(group.value, group.count)
  }
  const title = text(values, 'name') ?? text(values, 'code') ?? recordId

  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => void navigator.clipboard?.writeText(window.location.href).catch(() => undefined),
    },
  ]
  if (mayMove) {
    menu.unshift({ id: 'move', label: t('record.hr.dept.moveOpen'), icon: 'arrow-right', onSelect: () => setMoving(true) })
  }

  const own = counts.get(recordId)
  const facts: Fact[] = [
    {
      id: 'headcount',
      label: t('record.hr.dept.headcount'),
      value: own === undefined ? '—' : String(own),
      sub: headcount.isError ? t('record.hr.dept.headcountFailed') : t('record.hr.dept.headcountNote'),
    },
    {
      id: 'children',
      label: t('record.hr.dept.children'),
      value: String(Math.max(nodes.length - 1, 0)),
      sub: nodes.filter((node) => node.level === (nodes[0]?.level ?? 0) + 1).map((node) => node.name).slice(0, 3).join(', ') || undefined,
    },
    {
      id: 'head',
      label: t('card.hr.dept.head'),
      value: labelOf(values, 'head_employee_id') ? <RefLink values={values} column="head_employee_id" entity="hr.employee" avatar /> : t('record.notSet'),
    },
    {
      id: 'cost-center',
      label: t('column.cost_center'),
      value: <span className="mono">{text(values, 'cost_center') ?? '—'}</span>,
    },
  ]
  if (mayReadVacancies) {
    facts.splice(1, 0, {
      id: 'vacancies',
      label: t('record.hr.dept.openVacancies'),
      value: vacancies.data ? String(vacancies.data.rows.length) : '—',
    })
  }

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[
          { label: t('nav.departments'), to: listTo },
          ...(labelOf(values, 'parent_id') && text(values, 'parent_id')
            ? [{ label: labelOf(values, 'parent_id') as string, to: cardHref(user, 'hr.department', text(values, 'parent_id') as string) ?? undefined }]
            : []),
          { label: title },
        ]}
        title={title}
        meta={[
          labelOf(values, 'head_employee_id') ? (
            <span key="head" className="row" style={{ gap: 6 }}>
              {t('record.hr.dept.headMeta')} <RefLink values={values} column="head_employee_id" entity="hr.employee" avatar />
            </span>
          ) : null,
          labelOf(values, 'parent_id') ? (
            <span key="parent" className="row" style={{ gap: 6 }}>
              {t('record.hr.dept.partOf')} <Icon name="building" size="sm" />
              <RefLink values={values} column="parent_id" entity="hr.department" />
            </span>
          ) : (
            <span key="root" className="muted">
              {t('record.hr.dept.root')}
            </span>
          ),
          text(values, 'cost_center') ? (
            <span key="cc">
              {t('record.hr.dept.costCenterMeta')} <span className="mono" style={{ color: 'var(--fg)' }}>{text(values, 'cost_center')}</span>
            </span>
          ) : null,
        ]}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OutcomeAlerts failure={outcome.failure} notice={outcome.notice} />

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'structure', label: t('record.hr.dept.structure') },
          { id: 'staff', label: t('record.hr.dept.staff'), count: own ?? null },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.hr.dept.sections')}
      >
        <div className="layout-2-wide" style={{ paddingTop: 14 }}>
          <div className="rec-col">
            {tab === 'structure' && <OrgTree nodes={nodes} selectedId={recordId} counts={headcount.isSuccess ? counts : null} />}
            {tab === 'staff' && <StaffPanel departmentId={recordId} />}
            {tab === 'history' && (
              <Panel title={t('record.history.changes')}>
                <HistoryPanel entity={context.entity} recordId={recordId} />
              </Panel>
            )}
          </div>
          <div className="rec-col">
            <Panel title={t('record.hr.dept.about')}>{form}</Panel>
            {mayReadVacancies && (
              <Panel title={t('record.hr.dept.openVacancies')} count={vacancies.data?.rows.length ?? null} flush>
                {vacancies.isLoading ? (
                  <div className="panel-b muted">{t('app.loading')}</div>
                ) : vacancies.isError ? (
                  <StateView compact kind="error" title={t('record.hr.dept.vacanciesFailed')} />
                ) : (vacancies.data?.rows ?? []).length === 0 ? (
                  <div className="panel-b secondary">{t('record.hr.dept.noVacancies')}</div>
                ) : (
                  (vacancies.data?.rows ?? []).map((row) => <VacancyItem key={row.id} row={row} />)
                )}
              </Panel>
            )}
          </div>
        </div>
      </RecordTabs>

      {moving && (
        <MoveDialog
          departmentId={recordId}
          name={title}
          onClose={() => setMoving(false)}
          onDone={() => {
            setMoving(false)
            outcome.succeed(t('card.hr.dept.moved'))
          }}
        />
      )}
    </div>
  )
}

/**
 * Оргструктура деревом (`.tree`): узел, руководитель, численность, центр затрат.
 *
 * `counts == null` — численность не получена; тогда прочерк, а не ноль: «никого нет» и
 * «не знаем» — разные утверждения.
 */
export function OrgTree({
  nodes,
  selectedId,
  counts,
}: {
  nodes: DepartmentNode[]
  selectedId: string
  counts: Map<string, number> | null
}) {
  const { t, user } = useApp()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const heads = useEmployeeNames(nodes.map((node) => node.headEmployeeId))
  const base = nodes[0]?.level ?? 0

  const visible = useMemo(() => {
    const result: { node: DepartmentNode; parent: boolean }[] = []
    let hideDeeperThan: number | null = null
    nodes.forEach((node, index) => {
      if (hideDeeperThan != null) {
        if (node.level > hideDeeperThan) return
        hideDeeperThan = null
      }
      const next = nodes[index + 1]
      const parent = next !== undefined && next.level > node.level
      result.push({ node, parent })
      if (parent && collapsed.has(node.id)) hideDeeperThan = node.level
    })
    return result
  }, [nodes, collapsed])

  if (nodes.length === 0) {
    return (
      <Panel title={t('record.hr.dept.structure')}>
        <StateView compact kind="empty" title={t('card.hr.dept.empty')} />
      </Panel>
    )
  }

  const toggle = (id: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const parents = visible.filter((entry) => entry.parent).map((entry) => entry.node.id)

  return (
    <section className="panel" style={{ overflow: 'hidden' }} aria-label={t('record.hr.dept.structure')}>
      <div className="panel-h">
        <span className="h2">{t('record.hr.dept.structure')}</span>
        <span className="count">{t('record.hr.dept.units', { count: nodes.length })}</span>
        <span className="spacer" />
        {parents.length > 0 || collapsed.size > 0 ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setCollapsed(collapsed.size > 0 ? new Set() : new Set(nodes.map((node) => node.id)))}
          >
            {collapsed.size > 0 ? t('record.hr.dept.expandAll') : t('record.hr.dept.collapseAll')}
          </button>
        ) : null}
      </div>
      <div className="tree" role="tree" aria-label={t('record.hr.dept.structure')}>
        <div className="tree-head hr-tree-grid">
          <span>{t('record.hr.dept.unit')}</span>
          <span>{t('card.hr.dept.head')}</span>
          <span className="num">{t('record.hr.dept.headcount')}</span>
          <span>{t('record.hr.dept.cc')}</span>
        </div>
        {visible.map(({ node, parent }) => {
          const selected = node.id === selectedId
          const href = selected ? null : cardHref(user, 'hr.department', node.id)
          const head = node.headEmployeeId ? nameOf(heads, node.headEmployeeId) : null
          const headHref = node.headEmployeeId ? cardHref(user, 'hr.employee', node.headEmployeeId) : null
          const count = counts ? counts.get(node.id) ?? 0 : null
          return (
            <div
              key={node.id}
              className={selected ? 'tree-row hr-tree-grid is-selected' : 'tree-row hr-tree-grid'}
              role="treeitem"
              aria-level={node.level - base + 1}
              aria-selected={selected}
              aria-expanded={parent ? !collapsed.has(node.id) : undefined}
            >
              {/* The server-computed level drives the indent; nothing here parses the path. */}
              <span className="tree-node" style={{ ['--lvl' as string]: String(node.level) }} data-testid={`tree-node-${node.id}`}>
                {parent ? (
                  <button
                    type="button"
                    className="tree-toggle"
                    aria-expanded={!collapsed.has(node.id)}
                    aria-label={t(collapsed.has(node.id) ? 'record.hr.dept.expand' : 'record.hr.dept.collapse', { name: node.name })}
                    onClick={() => toggle(node.id)}
                  >
                    <Icon name="chevron" />
                  </button>
                ) : (
                  <span className="tree-leaf" aria-hidden="true">
                    ·
                  </span>
                )}
                {href ? (
                  <Link to={href} style={{ color: 'var(--fg)' }} className={node.level === base ? 'strong' : undefined}>
                    {node.name}
                  </Link>
                ) : (
                  <span className="strong">{node.name}</span>
                )}
                {node.code && <span className="muted t-sm nowrap">· {node.code}</span>}
              </span>
              <span className="row" style={{ gap: 6, minWidth: 0 }}>
                {head ? (
                  <>
                    <span className="ava" aria-hidden="true">
                      {initials(head)}
                    </span>
                    {headHref ? (
                      <Link to={headHref} className="ellipsis" style={{ color: 'var(--fg-secondary)' }}>
                        {head}
                      </Link>
                    ) : (
                      <span className="ellipsis">{head}</span>
                    )}
                  </>
                ) : (
                  <span className="muted">—</span>
                )}
              </span>
              <span className="num">{count == null ? '—' : count}</span>
              <span className="mono t-sm muted">{node.costCenter ?? '—'}</span>
            </div>
          )
        })}
      </div>
      <div className="panel-f muted">{t('record.hr.dept.treeNote')}</div>
    </section>
  )
}

/** Сотрудники отдела — механизмом списков: та же область видимости, что в списке сотрудников. */
function StaffPanel({ departmentId }: { departmentId: string }) {
  const { t, user, locale } = useApp()
  const query = useQuery({
    queryKey: ['hr-dept-staff', departmentId],
    retry: false,
    queryFn: ({ signal }) =>
      fetchPage(
        'hr.employee',
        {
          columns: ['last_name', 'first_name', 'personnel_number', 'position_id', 'hired_on', 'status'],
          filters: [{ column: 'department_id', operator: 'eq', value: departmentId }],
          sort: [{ column: 'last_name', descending: false }],
          pageSize: 100,
        },
        null,
        signal,
      ),
  })
  return (
    <Panel title={t('record.hr.dept.staffTitle')} count={query.data?.rows.length ?? null} flush>
      <Section query={query} errorTitle={t('record.hr.dept.staffFailed')}>
        {(page) =>
          page.rows.length === 0 ? (
            <StateView compact kind="empty" title={t('record.hr.dept.noStaff')} />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('card.hr.emp.name')}</th>
                  <th>{t('card.hr.emp.position')}</th>
                  <th>{t('column.personnel_number')}</th>
                  <th>{t('column.hired_on')}</th>
                  <th>{t('column.status')}</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => {
                  const name = [text(row, 'last_name'), text(row, 'first_name')].filter(Boolean).join(' ')
                  const href = cardHref(user, 'hr.employee', row.id)
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className="row" style={{ gap: 8 }}>
                          <span className="ava" aria-hidden="true">
                            {initials(name)}
                          </span>
                          {href ? <Link to={href}>{name}</Link> : name}
                        </span>
                      </td>
                      <td>{labelOf(row, 'position_id') ?? '—'}</td>
                      <td className="mono muted">{text(row, 'personnel_number') ?? '—'}</td>
                      <td>{day(text(row, 'hired_on'), locale)}</td>
                      <td>
                        <StatusBadge entity="hr.employee" status={text(row, 'status')} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )
        }
      </Section>
      {query.data?.hasMore && <HasMoreNote />}
    </Panel>
  )
}

function HasMoreNote() {
  const { t } = useApp()
  return <div className="panel-f muted">{t('record.hr.dept.staffMore')}</div>
}

function VacancyItem({ row }: { row: ListRow }) {
  const { t, user, locale } = useApp()
  const href = cardHref(user, 'hr.vacancy', row.id)
  const titleText = text(row, 'title') ?? row.id
  return (
    <div className="list-item" style={{ alignItems: 'flex-start' }}>
      <Icon name="user" className="muted" />
      <div className="stack" style={{ flex: 1, gap: 2 }}>
        {href ? (
          <Link to={href} className="strong">
            {titleText}
          </Link>
        ) : (
          <span className="strong">{titleText}</span>
        )}
        <span className="t-xs muted">
          {[
            text(row, 'opened_on') ? t('record.hr.vac.openedOn', { date: day(text(row, 'opened_on'), locale) }) : null,
            labelOf(row, 'recruiter_id') ? t('record.hr.vac.recruiterShort', { name: labelOf(row, 'recruiter_id') as string }) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
      <CellValue values={row} column="status" descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }} entity="hr.vacancy" />
    </div>
  )
}

/**
 * Перенос в другое подразделение. Пустой выбор — «в корень», и это выбор, а не отмена:
 * отправляется `null`, как того ждёт сервер.
 */
export function MoveDialog({
  departmentId,
  name,
  onClose,
  onDone,
}: {
  departmentId: string
  name: string
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useApp()
  const [target, setTarget] = useState<{ id: string; label: string } | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const move = useMutation({
    mutationFn: () => moveDepartment(departmentId, target ? target.id : null),
    onSuccess: onDone,
    onError: (error) => setFailure(failureText(error, t)),
  })
  return (
    <FormModal
      open
      title={t('record.hr.dept.moveTitle', { name })}
      subtitle={t('record.hr.dept.moveSubtitle')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('card.cancel')}
          </button>
          <button type="button" className="btn btn-primary" disabled={move.isPending} onClick={() => move.mutate()}>
            {target ? t('card.hr.dept.move') : t('record.hr.dept.moveToRoot')}
          </button>
        </>
      }
    >
      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      <div className="field">
        <label className="label" htmlFor="hr-move-target">
          {t('card.hr.dept.moveTo')}
        </label>
        <RecordPicker id="hr-move-target" entity="hr.department" searchColumn="name" value={target} onChange={setTarget} />
        <span className="help">{t('card.hr.dept.moveToRoot')}</span>
      </div>
    </FormModal>
  )
}
